import {
  getCarContextData,
  getInterActiveElementText,
  hasEventCarContext,
  isInterActiveElement,
  isInternalUrl
} from './analytic-helper';
import callOnce from './utils/call-once';
import debounce from './utils/debounce';

/**
 * Google Analytics 4 datalayer
 *
 * Note: This module collects events but only sends them to GA API if user has accepted "statistics" cookies from Cookiebot.
 */
class GA4 {
  /**
   * Client identifier
   */
  clientId;

  constructor() {
    this._delayedPushEvents = debounce(this.pushEvents.bind(this), 500);
  }

  /**
   * Push event object to datalayer
   *
   * @param {object[]|object} events data event objects
   */
  async sendEvent(events = []) {
    if (!Array.isArray(events)) {
      events = [events];
    }

    if (!('customDataLayer' in window)) {
      window.customDataLayer = [];
    }

    // Event have a page_type attribute that is page's URL
    events.forEach(event => {

      if( !event.params ) {
        event.params = {};
      }

      event.params['page_url'] = window?.location?.href;
      event.params['page_title'] = document.title;

      // Inject car context data if available
      if( hasEventCarContext(event.name) ) {
        const carData = getCarContextData();
        if( carData ) {
          event.params = { ...event.params, ...carData };
        }
      }
    });

    window.customDataLayer = [ ...window.customDataLayer, ...events ];

    this._delayedPushEvents();
  }

  /**
   * Post events to Google Analytics API.
   */
  async pushEvents() {
    // Make sure user has accepted Statistic Cookies from Cookiebot banner
    // CookieBot creates a CookieConsent instance to Window object.
    // We can use it to check if user has accepted statistics cookies.
    const statisticsAccepted = window.CookieConsent?.consent?.statistics || false;

    if (statisticsAccepted && window.customDataLayer?.length > 0) {
      try {
        await fetch('/stage-ajax/ga4/', {
          method: 'POST',
          body: JSON.stringify({
            client_id: this.getClientID(),
            events: [ ...window.customDataLayer ]
          })
        });

        // Empty sent events.
        window.customDataLayer = [];
      } catch (error) {
        console.info('Problem sending GA4 events forward...', error);
      }
    }
  }

  /**
   * Attach event listeners, send page visit event and so on. Performs routines
   * that need to be done after page load.
   */
  init() {
    this.attachEventListeners();
    this.sendPageVisit();
    this.sendFirstVisit();
  }

  /**
   * Return client identifier
   *
   * @returns {string|undefined} Google Analytic client ID
   */
  getClientID() {
    if (!window.ga || !window.ga.getAll || this.clientId) {
      return this.clientId;
    }

    window.ga.getAll().forEach(tracker => {
      this.clientId = tracker.get('clientId');
    });

    return this.clientId;
  }

  /**
   * Handle click event are taken from all interactive elements like
   * input, textareas, buttons and links.
   * @param {MouseEvent} event event object
   */
  handleClickEvent(event) {
    if( isInterActiveElement(event.target) ) {
      this.sendEvent({
        name: 'click',
        params: { 'click_text': getInterActiveElementText(event.target) }
      });
    }
  }

  /**
   * Attach document object event listeners.
   */
  attachEventListeners() {
    this.attachScrollListener();

    document.body.querySelectorAll('form').forEach( formElement => {
      formElement.addEventListener('submit', (event) => {
        this.sendEvent({ name: 'form_submit' });

        if( event.target instanceof HTMLFormElement ) {
          // Kontaktilomake
          if(event.target?.stage_form_id?.value === '897Q3LbTTEC3RNRPyEsizA') {
            this.sendEvent({ name: 'contact_form_submit' });
          }
        }
      });
    })

    document.body.addEventListener('click', (event) => {
      const {target} = event;

      this.handleClickEvent(event);

      if( target?.tagName === 'A' ) {
        if(target?.href?.includes('mailto')) {
          this.sendEvent({ name: 'email_click', params: { 'click_text': target.innerText } })
        } else if(target?.href?.includes('tel') ) {
          this.sendEvent({ name: 'phone_click', params: { 'click_text': target.innerText } })
        } else if( target?.href && !isInternalUrl(target.href) ) {
          this.sendEvent({ name: 'outbound_click', params: { 'click_text': target.innerText } })
        }
      }

      if( target?.tagName === 'BUTTON' && target?.title === 'Toggle fullscreen view') {
        this.sendEvent({ name: 'tab_info_click', params: { 'click_text': target?.title } })
      }

      if( target.dataset && 'ga4Name' in target.dataset ) {

        let text = target.innerText;

        if( 'ga4Text' in target.dataset ) {
          text = target.dataset['ga4Text'];
        }

        this.sendEvent({ name: target.dataset ['ga4Name'], params: { 'click_text': text } })
      }
    });
  }

  /**
   * Attach scroll event listener to document
   */
  attachScrollListener() {
    const sendEventOnce = callOnce(() => { this.sendEvent({ name: 'scroll' , params: { depth: 90 } }) })

    document.addEventListener('scroll', () => {
      const scrollPercentage = parseInt( 100 * (window.pageYOffset / ( document.body.scrollHeight - document.body.clientHeight )));
      if( scrollPercentage >= 90 ) {
        sendEventOnce();
      }
    });
  }

  /**
   * This event is send only when a user visit page first time.
   */
  sendFirstVisit() {
    if( window.localStorage.getItem('ga4_first_view') !== 'true' ) {
      this.sendEvent({ name: 'first_view'})
      window.localStorage.setItem('ga4_first_view', 'true');
    }
  }

  /**
   * This event is send every page visit
   *
   * @param {object} params parameters passed to event object, something like car page context object
   */
  sendPageVisit( params={} ) {
    this.sendEvent({ name: 'page_view', params: { ...params } });
  }
}

export { GA4 };
export default new GA4();
